import { IndividualWorkExperiencesAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";

interface DateEntryStructure {
  year: string;
  month: string;
  day: string;
  showsPresent: boolean;
  identifiedDateFormat: string;
}

export const checkEmploymentGapsAndOverlaps = (items: IndividualWorkExperiencesAnalysis[]) => {

  const itemsWithDates = items.filter(item => 
    item.startDateEntry && typeof item.startDateEntry === 'object' && 'year' in item.startDateEntry
  );
  if (itemsWithDates.length <= 1) {
    return { gaps: [], overlaps: [] };
  }
  
  // Sort items by start date (newest first)
  const sortedItems = [...itemsWithDates].sort((a, b) => {
    // Create dates from the DateEntry objects
    // Use type assertion to work with the actual structure
    const aEntry = a.startDateEntry as unknown as DateEntryStructure;
    const bEntry = b.startDateEntry as unknown as DateEntryStructure;
    
    const dateA = new Date(
      parseInt(aEntry.year),
      aEntry.month ? parseInt(aEntry.month) - 1 : 0,
      aEntry.day ? parseInt(aEntry.day) : 1
    );
    
    const dateB = new Date(
      parseInt(bEntry.year),
      bEntry.month ? parseInt(bEntry.month) - 1 : 0,
      bEntry.day ? parseInt(bEntry.day) : 1
    );
    
    return dateB.getTime() - dateA.getTime(); // Newest first
  });

  const gaps: number[] = [];
  const overlaps: number[] = [];

  // Check for gaps and overlaps
  for (let i = 0; i < sortedItems.length - 1; i++) {
    try {
      let currentEndDate: Date;
      
      // Use type assertion to work with the actual structure
      const endDateEntry = sortedItems[i].endDateEntry as unknown as DateEntryStructure;
      
      // Check if current job is present/ongoing
      if (endDateEntry && endDateEntry.showsPresent) {
        // If job is ongoing, use current date
        currentEndDate = new Date();
      } else if (endDateEntry && endDateEntry.year) {
        // Create a date from the endDateEntry
        currentEndDate = new Date(
          parseInt(endDateEntry.year),
          endDateEntry.month ? parseInt(endDateEntry.month) - 1 : 0,
          endDateEntry.day ? parseInt(endDateEntry.day) : 28  // Use end of month to be safer
        );
      } else {
        // If no end date is specified, assume it's the current date
        currentEndDate = new Date();
      }
      
      // Create a date from the next job's startDateEntry
      // Use type assertion to work with the actual structure
      const nextStartDateEntry = sortedItems[i + 1].startDateEntry as unknown as DateEntryStructure;
      const nextStartDate = new Date(
        parseInt(nextStartDateEntry.year),
        nextStartDateEntry.month ? parseInt(nextStartDateEntry.month) - 1 : 0,
      );
      // Skip invalid dates
      if (isNaN(nextStartDate.getTime()) || isNaN(currentEndDate.getTime())) continue;
      
      // Calculate difference in months - this is end date of newer job minus start date of older job
      // Positive means overlap (newer job ended after older job started)
      // Negative means gap (newer job ended before older job started)
      const diffMonths = (currentEndDate.getFullYear() - nextStartDate.getFullYear()) * 12 + 
                         (currentEndDate.getMonth() - nextStartDate.getMonth());
      
      // For special case "June 2022 - ongoing" and "Jan 2022 - Mar 2022"
      // We need to check if this specific pattern exists:
      // 1. Current job is ongoing
      // 2. There's an earlier job (next job in our sorted array) that ended before current job started
      // This creates a gap between the end of the earlier job and start of current job
      const isOngoingToEarlierGap = 
        endDateEntry?.showsPresent && // Current job is ongoing
        sortedItems[i+1].endDateEntry && // Next (earlier) job has an end date
        (() => {
          const nextEndDateEntry = sortedItems[i+1].endDateEntry as unknown as DateEntryStructure;
          if (!nextEndDateEntry.year) return false;
          
          const nextEndDate = new Date(
            parseInt(nextEndDateEntry.year),
            nextEndDateEntry.month ? parseInt(nextEndDateEntry.month) - 1 : 0,
            nextEndDateEntry.day ? parseInt(nextEndDateEntry.day) : 28
          );
          
          // Get start date of current job
          const currentStartEntry = sortedItems[i].startDateEntry as unknown as DateEntryStructure;
          const currentStartDate = new Date(
            parseInt(currentStartEntry.year),
            currentStartEntry.month ? parseInt(currentStartEntry.month) - 1 : 0,
            currentStartEntry.day ? parseInt(currentStartEntry.day) : 1
          );
          
          // Calculate months between next job's end and current job's start
          const gapMonths = (currentStartDate.getFullYear() - nextEndDate.getFullYear()) * 12 + 
                           (currentStartDate.getMonth() - nextEndDate.getMonth());
          
          return gapMonths > 2; // Gap is more than 2 months
        })();
      
      // Gap: If end date of current job is more than 2 months before start date of the next job
      // OR if we have the special case of an ongoing job with a gap from an earlier job
      if (diffMonths < -2 || isOngoingToEarlierGap) {
        // Find index of the job that has the gap AFTER it
        const originalIndex = items.findIndex(item => {
          if (!item.startDateEntry) return false;
          
          // Use type assertions for safe property access
          const itemStart = item.startDateEntry as unknown as DateEntryStructure;
          const compareStart = sortedItems[i + 1].startDateEntry as unknown as DateEntryStructure;
          
          return itemStart.year === compareStart.year && 
                 itemStart.month === compareStart.month;
        });
        
        if (originalIndex >= 0) gaps.push(originalIndex);
      }

      // Check for overlaps in these situations:
      // 1. Normal overlap: End date of current job is more than 2 months after start date of the next job
      // 2. Multiple "present" jobs: Both jobs are ongoing and have start dates within 2 months of each other
      
      const nextEndDateEntry = sortedItems[i + 1].endDateEntry as unknown as DateEntryStructure;
      const hasMultipleOngoingJobs = endDateEntry?.showsPresent && nextEndDateEntry?.showsPresent;
      
      // Calculate months between start dates if both are ongoing
      let startDateDiffMonths = 0;
      let hasIdenticalStartDates = false;
      
      if (hasMultipleOngoingJobs) {
        const currentStartEntry = sortedItems[i].startDateEntry as unknown as DateEntryStructure;
        const nextStartEntry = sortedItems[i + 1].startDateEntry as unknown as DateEntryStructure;
        
        // Check if start dates are identical (same year and month)
        hasIdenticalStartDates = 
          currentStartEntry.year === nextStartEntry.year && 
          currentStartEntry.month === nextStartEntry.month;
        
        const currentStartDate = new Date(
          parseInt(currentStartEntry.year),
          currentStartEntry.month ? parseInt(currentStartEntry.month) - 1 : 0,
          currentStartEntry.day ? parseInt(currentStartEntry.day) : 1
        );
        
        const nextStartDate = new Date(
          parseInt(nextStartEntry.year),
          nextStartEntry.month ? parseInt(nextStartEntry.month) - 1 : 0,
          nextStartEntry.day ? parseInt(nextStartEntry.day) : 1
        );
        
        startDateDiffMonths = Math.abs(
          (currentStartDate.getFullYear() - nextStartDate.getFullYear()) * 12 + 
          (currentStartDate.getMonth() - nextStartDate.getMonth())
        );
      }
      
      // Detect overlap if:
      // 1. Normal case: Positive diffMonths (overlap) and not an ongoing job
      // 2. Special case: Multiple ongoing jobs with similar start dates (within 2 months)
      // 3. Special case: Multiple ongoing jobs with identical start dates (e.g., Jun 2025 - Present and Jun 2025 - Present)
      if ((diffMonths > 2 && !(endDateEntry && endDateEntry.showsPresent)) || 
          (hasMultipleOngoingJobs && (startDateDiffMonths < 2 || hasIdenticalStartDates))) {
        
        // Find original index of the job with the overlap
        const originalIndex = items.findIndex(item => {
          if (!item.startDateEntry) return false;
          
          // Use type assertions for safe property access
          const itemStart = item.startDateEntry as unknown as DateEntryStructure;
          const compareStart = sortedItems[i].startDateEntry as unknown as DateEntryStructure;
          
          return itemStart.year === compareStart.year && 
                 itemStart.month === compareStart.month;
        });
        
        if (originalIndex >= 0) overlaps.push(originalIndex);
      }
    } catch (error) {
      console.error("Error checking gaps and overlaps:", error);
      continue;
    }
  }

  return { gaps, overlaps };
};

